import axios from 'axios';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8080',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    // if (error.response?.status === 401) {
    //   // Token expired or invalid
    //   localStorage.removeItem('token');
    //   localStorage.removeItem('email');
    //   window.location.href = '/login';
    // } else if (error.response?.status === 403) {
    //   // Access Denied: try to refresh profile to get updated permissions
    //   console.warn('[403 Detected] Attempting to sync profile from backend...');
    //   await syncUserProfileFromBackend();
    //   // Don't auto-retry; let the component handle the error and re-attempt if needed
    // }
    
    // Log detailed error information
    console.error('API Error:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      message: error.message,
      data: error.response?.data,
      url: error.config?.url
    });
    return Promise.reject(error);
  }
);

// ============ AUTH ENDPOINTS ============
export const authAPI = {
  register: (data) => apiClient.post('/api/auth/register', data),
  login: (data) => apiClient.post('/api/auth/login', data),
  verifyEmail: (email, otp) => apiClient.post('/api/auth/verify-email', { email, otp }),
  resendVerification: (email) => apiClient.post('/api/auth/resend-verification', { email }),
  getAllUsers: () => apiClient.get('/api/auth/users'),
  updateUserStatus: (userId, status) => apiClient.put(`/api/auth/users/${userId}/status?status=${status}`),
  getProfile: (email) => apiClient.get(`/api/auth/myprofile?email=${email}`),
  forgotPassword: (email) => apiClient.post(`/api/auth/forgot-password?email=${email}`),
  resetPassword: (token, newPassword) => apiClient.post(`/api/auth/reset-password/${token}`, { newPassword }),
  logout: () => apiClient.post('/api/auth/logout'),
  // ✅ Google OAuth APIs
  connectGoogle: (userId) =>
    apiClient.get(`/api/oauth2/google/url?userId=${userId}`),
  disconnectGoogle: (userId) =>
    apiClient.delete(`/api/oauth2/google/disconnect?userId=${userId}`),
  googleStatus: (userId) =>
    apiClient.get(`/api/oauth2/google/status?userId=${userId}`),
  connectMicrosoft: (userId) =>
    apiClient.get(`/api/oauth2/microsoft/url?userId=${userId}`),
  disconnectMicrosoft: (userId) =>
    apiClient.delete(`/api/oauth2/microsoft/disconnect?userId=${userId}`),
  microsoftStatus: (userId) =>
    apiClient.get(`/api/oauth2/microsoft/status?userId=${userId}`),
  getDefaultProvider: (userId) =>
  apiClient.get(`/api/oauth2/default?userId=${userId}`),
  setDefaultProvider: (userId, provider) =>
  apiClient.put(`/api/oauth2/default?userId=${userId}&provider=${provider}`),
  uploadLogo: (formData) => apiClient.post('/api/auth/profile/logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateProfile: (data) => apiClient.put('/api/auth/profile', data),
};

// ============ CONTACTS ENDPOINTS ============
export const contactsAPI = {
  // CRUD Operations
  addContact: (data) => apiClient.post('/api/contacts', data),
  getAllContacts: () => apiClient.get('/api/contacts'),
  getContactsPaged: (params) => apiClient.get('/api/contacts/paged', { params }),
  getContactById: (id) => apiClient.get(`/api/contacts/${id}`),
  getContactByEmail: (email) => apiClient.get(`/api/contacts/email/${email}`),
  updateContact: (id, data) => apiClient.put(`/api/contacts/${id}`, data),
  deleteContact: (id) => apiClient.delete(`/api/contacts/${id}`),
  
  // Search & Filter
  searchByName: (name) => apiClient.get(`/api/contacts/search?name=${name}`),
  getByLeadStage: (leadStage) => apiClient.get(`/api/contacts/lead-stage/${leadStage}`),
  
  // Special Operations
  migrateToClient: (id) => apiClient.post(`/api/contacts/${id}/migrate-to-client`),
  getMyContacts: () => apiClient.get('/api/contacts/my'),
  
  // Bulk Operations
  bulkDelete: (ids) => apiClient.delete('/api/contacts/delete', {
  data: ids,                          
  headers: { 
    'Content-Type': 'application/json' 
  }
}),
  updateContactOwner: (data) => apiClient.put('/api/contacts/update-assigned-to', data),
  bulkEdit: (data) => apiClient.put('/api/contacts/update-field', data),
  
  // Import/Export
  getColumns: () => apiClient.get('/api/contacts/columns'),
  generateTemplate: (columns) => apiClient.post('/api/contacts/generate-template', columns, {
    responseType: 'blob'
  }),
  importContacts: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/api/contacts/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  getNotes: (contactId) =>
    apiClient.get(`/api/contacts/${contactId}/notes`),

  // Update / Save Notes
  updateNotes: (contactId, notes) =>
    apiClient.put(`/api/contacts/${contactId}/notes`, { notes }),

  getAssociatedCompany: (contactId) =>
  apiClient.get(`/api/contacts/${contactId}/company`),

  checkEmailExists: (email) => apiClient.get('/api/contacts/check-email', { params: { email } }),
  checkPhoneExists: (phone) => apiClient.get('/api/contacts/check-phone', { params: { phone } }),
};

// ============ COMPANIES ENDPOINTS ============
export const companiesAPI = {
  // CRUD Operations
  addCompany: (data) => apiClient.post('/api/companies', data),
  getAllCompanies: () => apiClient.get('/api/companies'),
  getCompanyById: (id) => apiClient.get(`/api/companies/${id}`),
  updateCompany: (id, data) => apiClient.put(`/api/companies/${id}`, data),
  deleteCompany: (id) => apiClient.delete(`/api/companies/${id}`),
  
  // Pagination
  getCompaniesPaged: (params) => apiClient.get('/api/companies/paged', { params }),
  
  // Search & Filter
  searchByName: (name) => apiClient.get(`/api/companies/search?name=${name}`),
  filterByIndustry: (industry) => apiClient.get(`/api/companies/industry/${industry}`),
  filterByRelationship: (relationshipType) => apiClient.get(`/api/companies/relationship/${relationshipType}`),
  filterByLeadStatus: (leadStatus) => apiClient.get(`/api/companies/lead-status/${leadStatus}`),
  filterByLeadSource: (leadSource) => apiClient.get(`/api/companies/lead-source/${leadSource}`),
  filterByStage: (stage) => apiClient.get(`/api/companies/stage/${stage}`),
  getByCreator: (id) => apiClient.get(`/api/companies/by-user/${id}`),
  filterByDateRange: (start, end) => apiClient.get(`/api/companies/date-range?start=${start}&end=${end}`),
  searchByEmail: (email) => apiClient.get(`/api/companies/email/${email}`),
  searchByPhone: (phone) => apiClient.get(`/api/companies/phone/${phone}`),
  multiSearch: (params) => apiClient.get('/api/companies/multi-search', { params }),
  filterByCity: (city) => apiClient.get(`/api/companies/city/${city}`),
  filterByState: (state) => apiClient.get(`/api/companies/state/${state}`),
  filterByCountry: (country) => apiClient.get(`/api/companies/country/${country}`),
  getRecentActivity: (days) => apiClient.get(`/api/companies/recent-activity/${days}`),
  getContactableCompanies: () => apiClient.get('/api/companies/contactable'),
  
  // Special Operations
  updateLastActivity: (id) => apiClient.put(`/api/companies/${id}/activity`),
  getTotalCount: () => apiClient.get('/api/companies/stats/total'),
  
  // Bulk Operations
  bulkDelete: (ids) => apiClient.delete('/api/companies/deletebulk', { data: ids }),
  updateContactOwner: (data) => apiClient.put('/api/companies/update-contact-owner', data),
  bulkEdit: (data) => apiClient.put('/api/companies/update-bulk-field', data),
  
  // Import/Export
  getColumns: () => apiClient.get('/api/companies/columns'),
  generateTemplate: (columns) => apiClient.post('/api/companies/generate-template', columns, {
    responseType: 'blob'
  }),
  importCompanies: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/api/companies/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  getNotes: (companyId) =>
    apiClient.get(`/api/companies/${companyId}/notes`),

  // Update / Save Notes
  updateNotes: (companyId, notes) =>
    apiClient.put(`/api/companies/${companyId}/notes`, { notes }),

  getLinkedContacts: (companyId) =>
  apiClient.get(`/api/companies/${companyId}/contacts`),
};

// ============ CLIENTS ENDPOINTS ============
export const clientsAPI = {
  // CRUD Operations
  addClient: (data) => {
    return apiClient.post('/api/clients', data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getAllClients: (params) => apiClient.get('/api/clients', { params }),
  getClientsPaged: (params) => apiClient.get('/api/clients/paged', { params }),
  getMigratedIds: () => apiClient.get('/api/clients/migrated-ids'),
  getMyClients: (params) => apiClient.get('/api/clients/my-clients', { params }),
  getClientById: (id) => apiClient.get(`/api/clients/${id}`),
  updateClient: (id, data) => {
    return apiClient.put(`/api/clients/${id}`, data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteClient: (id) => apiClient.delete(`/api/clients/${id}`),
  
  // Search & Filter
  searchByName: (name) => apiClient.get(`/api/clients/search?name=${name}`),
  filterByEmail: (email) => apiClient.get(`/api/clients/email/${email}`),
  filterByRole: (role) => apiClient.get(`/api/clients/role/${role}`),
  
  // Special Operations
  migrateClient: (data) => apiClient.post(`/api/clients/migrate`, data, {
  headers: { 'Content-Type': 'multipart/form-data' }
}),
  checkMigrated: (id) => apiClient.get(`/api/clients/${id}/check-migrated`),
  getTotalCount: () => apiClient.get('/api/clients/total-count'),
  
  // Bulk Operations
  bulkDeleteClients: (ids) => apiClient.delete('/api/clients/bulk-delete', { data: ids }),
  bulkAssignClients: (data) => apiClient.put('/api/clients/bulk-update/contact-owner', data),
  bulkEditClients: (data) => apiClient.put('/api/clients/bulk-update/field', data),
  
  // Import/Export
  getAllColumns: () => apiClient.get('/api/clients/excel/columns'),
  generateTemplate: (selectedColumns) => apiClient.post(
  '/api/clients/excel/template',
  selectedColumns,
  { responseType: 'blob' }
),
  importClients: (formData) => {
    return apiClient.post('/api/clients/excel/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  checkEmailExists: (email) => apiClient.get('/api/clients/check-email', { params: { email } }),
  checkPhoneExists: (phone) => apiClient.get('/api/clients/check-phone', { params: { phone } }),
};

// ============ TASKS ENDPOINTS ============
export const tasksAPI = {
  // CRUD Operations
  createTask: (data) => apiClient.post('/api/tasks', data),
  getAllTasks: () => apiClient.get('/api/tasks/all'),
  getTasksPaged: (params) => apiClient.get('/api/tasks/paged', { params }),
  updateTask: (id, data) => apiClient.patch(`/api/tasks/${id}`, data),
  deleteTask: (id) => apiClient.delete(`/api/tasks/${id}`),
  
  // Filter by User
  getMyTasks: () => apiClient.get('/api/tasks/my'),
  
  // Filter by Date - Using getAllTasks for now, filter on client side or update when endpoints are available
  getTodayTasks: () => apiClient.get('/api/tasks/my'),
  getUpcomingTasks: () => apiClient.get('/api/tasks/my'),
  getOverdueTasks: () => apiClient.get('/api/tasks/my'),
  
  // Filter by Date and User
  getTodayTasksByUser: (userId) => apiClient.get(`/api/tasks/my`),
  getUpcomingTasksByUser: (userId) => apiClient.get(`/api/tasks/my`),
  getOverdueTasksByUser: (userId) => apiClient.get(`/api/tasks/my`),

  bulkDelete: (ids) => apiClient.delete('/api/tasks/bulkdelete', { data: ids }),
  bulkUpdate: (ids, fieldName, fieldValue) =>
    apiClient.put('/api/tasks/bulk-update', { ids, fieldName, fieldValue }),
  bulkAssign: (ids, newAssignee) =>
    apiClient.put('/api/tasks/bulk-assign', { ids, newAssignee }),

  gettasksbycompany:(id)=>apiClient.get(`/api/tasks/by-account/${id}`),
  gettasksbycontact:(id)=>apiClient.get(`/api/tasks/by-contact/${id}`)
};

// ============ USER MANAGEMENT ENDPOINTS ============
export const userAPI = {
  // User CRUD operations
  /**
   * Get all users (for dropdowns, returns array of users with _id and name)
   */
  getAllUsers: () => apiClient.get('/api/auth/users'),
  addUser: (data) => apiClient.post('/api/auth/register', data),
  getUserById: (id) => apiClient.get(`/api/auth/users/${id}`),
  updateUser: (id, data) => apiClient.put(`/api/auth/users/${id}`, data),
  deleteUser: (id) => apiClient.delete(`/api/auth/users/${id}`),
  
  // User status management
  activateUser: (id) => apiClient.patch(`/api/auth/users/${id}/activate`),
  inactivateUser: (id) => apiClient.patch(`/api/auth/users/${id}/inactivate`),
  updateUserStatus: (id, status) => apiClient.put(`/api/auth/users/${id}/action-status?actionStatus=${status}`),
  
  // User role and permissions
  updateUserRole: (id, role) => apiClient.put(`/api/auth/users/${id}/role`, { role }),
  updateUserCount: (id, noOfUsers) => apiClient.put(`/api/auth/users/${id}/user-count?noOfUsers=${noOfUsers}`),
};

// ============ JOB MANAGEMENT ENDPOINTS ============
export const jobsAPI = {
  // Job CRUD operations
  createJob: (data) => apiClient.post('/api/job-postings', data),
  getAllJobs: () => apiClient.get('/api/job-postings'),
  getJobById: (id) => apiClient.get(`/api/job-postings/${id}`),
  getJobByCode: (jobCode) => apiClient.get(`/api/job-postings/job-code/${jobCode}`),
  getJobsByClientId:(clientId)=>apiClient.get(`/api/job-postings/client/${clientId}`),
  updateJob: (id, data) => apiClient.put(`/api/job-postings/${id}`, data),
  deleteJob: (id) => apiClient.delete(`/api/job-postings/${id}`),
  
  // Bulk operations
  bulkDeleteJobs: (ids) => apiClient.delete('/api/job-postings/bulk-delete', { data: ids }),
  bulkAssignJobs: (data) => apiClient.put('/api/job-postings/update-assigned-to', data),
  bulkEditJobs: (data) => apiClient.put('/api/job-postings/bulk-update/field', data),
  
  // Job search and filter
  searchJobs: (searchTerm) => apiClient.get(`/api/job-postings/search?q=${searchTerm}`),
  filterByStatus: (status) => apiClient.get(`/api/job-postings/status/${status}`),
  filterByClient: (client) => apiClient.get(`/api/job-postings/client/${client}`),
  
  // My Jobs
  getMyJobs: () => apiClient.get('/api/job-postings/my'),
};

// ============ CANDIDATE/APPLICATION MANAGEMENT ENDPOINTS ============
export const candidatesAPI = {
  // Field Configuration
   saveOrUpdateFieldConfig: (data) => apiClient.post('/api/field-configs', data),
  getAvailableFields: () => apiClient.get('/api/applications/fields/available'),
  configureFields: (orgId, data) => apiClient.post(`/api/field-configs/organization/${orgId}`, data),
  getFieldConfig: () => apiClient.get('/api/field-configs'),
  updateFieldConfig: (data) => apiClient.post('/api/field-configs', data),
  deleteFieldConfig: () => apiClient.delete('/api/field-configs'),
  checkConfigExists: () => apiClient.get('/api/field-configs/exists'),
  getFields: () => apiClient.get('/api/field-configs/fields'),
  
  // Template Operations
  downloadTemplate: () => apiClient.get('/api/field-configs/template/download', {
    responseType: 'blob'
  }),
  
  // Application/Candidate CRUD Operations
  createApplication: (data) => {
    return apiClient.post('/api/applications', data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  updateApplication: (id, data) => apiClient.put(`/api/applications/${id}`, data),
  getApplicationById: (id) => apiClient.get(`/api/applications/${id}`),
  getApplicationByCustomId: (applicationId) => apiClient.get(`/api/applications/application-id/${applicationId}`),
  deleteApplication: (id) => apiClient.delete(`/api/applications/${id}`),
  
  // Get Applications by filters
  getAllApplicationsByOrg: () => apiClient.get('/api/applications'),
  getApplicationsByJobId: (jobId) => apiClient.get(`/api/applications/job/${jobId}`),
  getApplicationsByClientId: (clientId) => apiClient.get(`/api/applications/client/${clientId}`),
  getApplicationsByJobAndClient: (jobId, clientId) => apiClient.get(`/api/applications/job/${jobId}/client/${clientId}`),
  
  // Status Management
  updateApplicationStatus: (id, payload) => apiClient.patch(`/api/applications/${id}/status`, payload),
  bulkUpdateStatus: (data) => apiClient.patch('/api/applications/bulk-update-status', data),
  
  // Move/Recommend Candidate to a Job
  addJobToApplication: (applicationId, data) => apiClient.post(`/api/applications/${applicationId}/add-job`, data),
  moveCandidateToJob: (data) => apiClient.put('/api/applications/move-to-job', data),
  
  // Delete Operations
  deleteJobFromApplication: (applicationId, jobId) => apiClient.delete(`/api/applications/${applicationId}/job/${jobId}`),
  
  // Import Operations
  importCandidates: (formData) => {
    return apiClient.post('/api/applications/bulk-import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  // Shareable Links
  
  generateShareableLink: (data) => apiClient.post('/api/shareable-links', data),
  getLinks: () => apiClient.get(`/api/shareable-links`),
  getActiveLinks: () => apiClient.get('/api/shareable-links/active'),
  revokeLink: (linkId) => 
    apiClient.patch(`/api/shareable-links/${linkId}/revoke`),
  extendLink: (linkId, hours) =>
    apiClient.patch(
    `/api/shareable-links/${linkId}/extend`,
    { hours }
  ),
 sendLinkEmail: (linkId) =>
    apiClient.post(`/api/shareable-links/${linkId}/send-link`),
  sendAttachmentEmail: (linkId) =>
    apiClient.post(`/api/shareable-links/${linkId}/send-attachment`),
  cleanupExpiredLinks: () => apiClient.delete('/api/shareable-links/cleanup/expired'),
  getLink:(linkId)=>apiClient.get(`/api/shareable-links/${linkId}`),

  //clientside apis
  verifyToken: (token) => apiClient.get(`/api/client-access/${token}/validate`),
  getApplicationsFromToken: (token) => apiClient.get(`/api/client-access/${token}/applications`),
  
  updateStatusByToken: (token, applicationId, payload) =>
  apiClient.patch(`/api/client-access/${token}/applications/${applicationId}/status`, 
    payload  
  ),
  
  //candidate slot selection apis
  getCandidateInterviewSlots: (applicationId, timezone) =>
    axios.get(`/api/interview-slots/${applicationId}`, {
      params: timezone ? { timezone } : {},
    }),
  confirmCandidateInterviewSlot: (applicationId, selectedSlot, userTimeZone) =>
    axios.post(`/api/interview-slots/${applicationId}/select`, {
      selectedSlot,
      userTimeZone,
    }),

   
  getConvertedSlotsForCandidate: (candidateApplicationId, userTimeZone) =>
    apiClient.get(`/api/interviews/${candidateApplicationId}/converted-slots`, {
    params: { userTimeZone: userTimeZone },
    headers: {
      "X-User-Id": localStorage.getItem("userId")
    }
  }),

 scheduleInterview: (data) =>
  apiClient.post(
    "/api/interviews/schedule",
    data,
    {
      headers: {
        "X-User-Id": localStorage.getItem("userId")
      }
    }
  ),

  checkEmailExists: (email) => apiClient.get('/api/applications/check-email', { params: { email } }),
  checkPhoneExists: (phone) => apiClient.get('/api/applications/check-phone', { params: { phone } }),
};

// ============ BULK EMAIL ENDPOINTS ============
export const bulkEmailAPI = {
  // Send bulk email to selected entity IDs (CONTACT / COMPANY / CLIENT)
  sendByIds: (entityType, entityIds, subject, body, attachments = []) => {
    const formData = new FormData();
    formData.append('entityType', entityType);
    entityIds.forEach(id => formData.append('entityIds', id));
    formData.append('subject', subject);
    formData.append('body', body);
    attachments.forEach(file => formData.append('attachments', file));
    return apiClient.post('/api/bulk-email/send', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Send bulk email via Excel file upload
  sendByExcel: (entityType, excelFile, subject, body, attachments = []) => {
    const formData = new FormData();
    formData.append('entityType', entityType);
    formData.append('excelFile', excelFile);
    formData.append('subject', subject);
    formData.append('body', body);
    attachments.forEach(file => formData.append('attachments', file));
    return apiClient.post('/api/bulk-email/send-excel', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Get all email logs for the organization
  getLogs: () => apiClient.get('/api/bulk-email/logs'),

  // Get logs filtered by status: PENDING | SUCCESS | PARTIAL_FAILED | FAILED
  getLogsByStatus: (status) => apiClient.get(`/api/bulk-email/logs/status/${status}`),

  // Get a single email log by ID
  getLogById: (emailLogId) => apiClient.get(`/api/bulk-email/logs/${emailLogId}`),

  // Send a single email to a direct address (used by EmailTemplatePanel)
  sendDirect: ({ email, name, subject, body }) =>
    apiClient.post('/api/bulk-email/send-direct', { email, name, subject, body }),
};

// ============ TEMPLATE LIBRARY ENDPOINTS ============
export const templatesAPI = {
  getTemplates: (organizationId, channel, module) => {
    const params = {};
    if (channel) params.channel = channel;
    if (module) params.module = module;
    return apiClient.get('/api/templates', { params });
  },
  createTemplate: (data) => apiClient.post('/api/templates', data),
  updateTemplate: (id, data) => apiClient.put(`/api/templates/${id}`, data),
  deleteTemplate: (id) => apiClient.delete(`/api/templates/${id}`),
  seedTemplates: () => apiClient.post('/api/templates/seed'),
};

// ============ EMPLOYEE MANAGEMENT ENDPOINTS ============
export const employeesAPI = {
  // CRUD Operations
  createEmployee: (data) => apiClient.post('/api/employees/addEmployee', data),
  getAllEmployees: () => apiClient.get('/api/employees'),
  getEmployeeById: (id) => apiClient.get(`/api/employees/${id}`),
  updateEmployee: (id, data) => apiClient.put(`/api/employees/${id}`, data),
  deleteEmployee: (id) => apiClient.delete(`/api/employees/${id}`),
  employeeLogin:(data)=>apiClient.post('/api/auth/employee/login',data),
  
  // Credentials Management
  resendCredentials: (employeeId, email) => apiClient.post(`/api/auth/forgot-password?email=${email}`),
  
  // Status Management - Use specific endpoints
  inactivateEmployee: (id) => apiClient.patch(`/api/employees/${id}/inactivate`),
  activateEmployee: (id) => apiClient.patch(`/api/employees/${id}/activate`),
  updateEmployeeStatus: (id, status) => {
    // Convert status to proper endpoint format
    const endpoint = status === 'ACTIVE' || status === 'active' 
      ? 'activate' 
      : 'inactivate';
    return apiClient.patch(`/api/employees/${id}/${endpoint}`);
  },
  
  // Bulk Reassign
  getAssignedCount: (id) => apiClient.get(`/api/employees/${id}/assigned-count`),
  reassignAndInactivate: (id, newEmployeeId) => apiClient.post(`/api/employees/${id}/reassign`, { newEmployeeId }),

  // Password Management
  updatePassword: (id, oldPassword, newPassword) => apiClient.put(`/api/employees/${id}/password`, {
    oldPassword,
    newPassword
  }),
  
  // Bulk Operations
  bulkDeleteEmployees: (ids) => apiClient.delete('/api/employees/bulk', { data: ids }),
  bulkInactivateEmployees: (ids) => apiClient.patch('/api/employees/bulk-inactivate', ids),
  bulkActivateEmployees: (ids) => apiClient.patch('/api/employees/bulk-activate', ids),
};

// ============ INVOICE ENDPOINTS ============
export const invoiceAPI = {
  // CRUD Operations
  createInvoice: (formData) => {
    return apiClient.post('/api/placement-orders', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getAllInvoices: (organizationId) => apiClient.get('/api/placement-orders', { params: { organizationId } }),
  getInvoiceById: (id) => apiClient.get(`/api/placement-orders/${id}`),
  updateInvoice: (id, formData) => {
    return apiClient.put(`/api/placement-orders/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteInvoice: (id) => apiClient.delete(`/api/placement-orders/${id}`),
resendInvoiceEmail: (id) =>
    apiClient.post(`/api/placement-orders/${id}/send-email`),
  getDocumentDownloadUrl: (fileUrl) => apiClient.get('/api/placement-orders/document-download-url', { params: { fileUrl } }),
  getPlacementOrderPdf: (id) => apiClient.get(`/api/placement-orders/${id}/pdf`, { responseType: 'blob' }),
}

  // ============ ITEMS MASTER ENDPOINTS ============
export const itemsAPI = {
  // CRUD Operations
  createItem: (formData) => {
    return apiClient.post('/api/items', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getAllItems: () => apiClient.get('/api/items'),
  getItemById: (id) => apiClient.get(`/api/items/${id}`),
  updateItem: (id, formData) => {
    return apiClient.put(`/api/items/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteItem: (id) => apiClient.delete(`/api/items/${id}`),
  
  // Search
  searchByName: (name) => apiClient.get(`/api/items/search`, { params: { name } }),
  searchByHsn: (hsn) => apiClient.get(`/api/items/search`, { params: { hsn } }),
  
  // Bulk Operations
  bulkUpload: (formData) => {
    return apiClient.post('/api/items/bulk-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  exportToExcel: () => apiClient.get('/api/items/export', {
    responseType: 'blob'
  }),
  downloadTemplate: () => apiClient.get('/api/items/template', {
    responseType: 'blob'
  }),
};

// ============ GENERIC INVOICE ENDPOINTS ============
export const genericInvoiceAPI = {

  // CREATE GENERIC INVOICE (JSON + Images)
  createInvoice: (formData) => {
    return apiClient.post('/api/invoices', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // GET ALL GENERIC INVOICES (Org comes from JWT automatically)
  getAllInvoices: () => apiClient.get('/api/invoices'),

  // (Optional future endpoints if added)
  getInvoiceById: (id) => apiClient.get(`/api/invoices/${id}`),

  updateInvoice: (id, formData) => {
    return apiClient.put(`/api/invoices/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  deleteInvoice: (id) => apiClient.delete(`/api/invoices/${id}`),
  getInvoicePdf: (id) => apiClient.get(`/api/invoices/invoice/${id}/pdf`, { responseType: 'blob' }),
};

// ============ SUPER ADMIN ENDPOINTS ============
export const superAdminAPI = {
  getAllUsers: () => apiClient.get('/api/auth/users'),
  updateUserStatus: (userId, actionStatus) =>
    apiClient.put(`/api/auth/users/${userId}/status?status=${actionStatus}`),
  updatePaymentStatus: (userId, paymentStatus) =>
    apiClient.put(`/api/auth/users/${userId}/payment-status?paymentStatus=${paymentStatus}`),
  updateUserCount: (userId, noOfUsers) =>
    apiClient.put(`/api/auth/users/${userId}/user-count?noOfUsers=${noOfUsers}`),
  getDashboardStats: () => apiClient.get('/api/auth/dashboard/stats'),
  resendActivationEmail: (userId) => apiClient.post(`/api/auth/users/${userId}/resend-activation-email`),
};



export default apiClient;
